import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Check, Zap, Activity, Info, X, AlertTriangle } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { Skeleton } from '@/components/ui';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ProjectDashboardPage = lazy(() => import('@/pages/ProjectDashboardPage'));
const LeadsPage = lazy(() => import('@/pages/LeadsPage'));
const LeadDetailPage = lazy(() => import('@/pages/LeadDetailPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const DailyActivityPage = lazy(() => import('@/pages/DailyActivityPage'));
const ContentPage = lazy(() => import('@/pages/ContentPage'));
const CampaignsPage = lazy(() => import('@/pages/CampaignsPage'));
const ExperimentsPage = lazy(() => import('@/pages/ExperimentsPage'));
const LearningsPage = lazy(() => import('@/pages/LearningsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const AgendaPage = lazy(() => import('@/pages/AgendaPage'));
const PlanDiarioPage = lazy(() => import('@/pages/PlanDiarioPage'));
const ProspectSearchPage = lazy(() => import('@/pages/ProspectSearchPage'));

function PageLoader() {
  return (
    <div className="space-y-4 p-1">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="p-8"><PageLoader /></div>}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="projects/:projectId" element={<ProjectDashboardPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:leadId" element={<LeadDetailPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="plan-del-dia" element={<PlanDiarioPage />} />
            <Route path="activity" element={<DailyActivityPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="experiments" element={<ExperimentsPage />} />
            <Route path="learnings" element={<LearningsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="prospect-search" element={<ProspectSearchPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster
        position="top-right"
        gap={8}
        icons={{
          success: <Check size={13} strokeWidth={2.5} />,
          error: <X size={13} strokeWidth={2.5} />,
          warning: <AlertTriangle size={13} strokeWidth={2} />,
          info: <Info size={13} strokeWidth={2} />,
          loading: <Activity size={13} strokeWidth={2} />,
        }}
      />
    </BrowserRouter>
  );
}
